import { ChevronLeft, Info, Landmark, ScanLine, Wallet, Zap } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { canPayBrebQr } from "@/app/auth/canPayBrebQr";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { QrScannerOverlay } from "@/modules/dashboard/breb-qr-pay/components/QrScannerOverlay";
import { useBrebQrResolution } from "@/modules/dashboard/breb-qr-pay/hooks/useBrebQrResolution";
import { quotePaysExactly } from "@/modules/dashboard/breb-qr-pay/utils/brebQrPayment";
import { isEmvQrPayload } from "@/modules/dashboard/breb-qr-pay/utils/emvQr";
import { detectQrInImage } from "@/modules/dashboard/breb-qr-pay/utils/qrDetector";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { PayoutsVerificationGate } from "@/modules/verification/components/PayoutsVerificationGate";
import { useOrganizationVerificationStatus } from "@/modules/verification/hooks/useOrganizationVerificationStatus";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { boundsForPayer, formatLimitAmount, isAmountLimitCode } from "@/shared/utils/amountLimits";
import {
  EMPTY_MASKED_AMOUNT,
  type MaskedAmount,
  maskDecimalString,
  maskForCurrency,
} from "@/shared/utils/amountMask";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  PAYOUT_WIZARD_COUNTRIES,
  resolveRuntimeCorridor,
  RUNTIME_CORRIDOR_ISO3,
  type RuntimeCorridor,
} from "../../constants/dispersions.constants";
import { brebDirectoryOutcome, useBrebKeyResolution } from "../../hooks/useBrebKeyResolution";
import { useWalletSendQuote } from "../../hooks/useWalletSendQuote";
import { useWalletSendSubmit } from "../../hooks/useWalletSendSubmit";
import { displayBrebKey, resolvedBrebKeyType } from "../../utils/brebKeyLookup";
import { colombiaRailUnavailableKey } from "../../utils/colombiaRailAvailability";
import { resolvePayoutLimitViolation } from "../../utils/payoutLimitCheck";
import {
  amountCurrencyFor,
  type BrebSendRecipient,
  isCrossCurrencySend,
  quoteMatchesSend,
  railsFor,
  type SendBreakdown,
  sendBreakdown,
  type SendFeeMode,
  sendPayerType,
  type SendRail,
  type SendRecipient,
  sendRouteFor,
} from "../../utils/walletSend";
import { DispersionResultScreens } from "../DispersionForm/DispersionResultScreens";
import { BrebKeyLookupField } from "./BrebKeyLookupField";
import { CbuCvuRecipientForm } from "./CbuCvuRecipientForm";
import { formatSendMoney, formatSendMoneyWithCode } from "./formatSendMoney";
import { PixRecipientForm } from "./PixRecipientForm";
import { ScannedQrRecipient } from "./ScannedQrRecipient";
import { SendAmountField } from "./SendAmountField";
import { SendMoneyTransit } from "./SendMoneyTransit";
import {
  type CbuCvuRecipientValues,
  EMPTY_CBU_CVU_VALUES,
  EMPTY_PIX_VALUES,
  EMPTY_SPEI_VALUES,
  type PixRecipientValues,
  type SpeiRecipientValues,
} from "./sendRecipients.schema";
import { SendReview } from "./SendReview";
import { SendRouteStrip } from "./SendRouteStrip";
import { SpeiRecipientForm } from "./SpeiRecipientForm";
import { EMPTY_TRANSFIYA_VALUES, type TransfiyaRecipientValues } from "./transfiyaRecipient.schema";
import { TransfiyaRecipientForm } from "./TransfiyaRecipientForm";

export interface WalletSendPanelProps {
  /** The wallet whose card opened the panel: the default origin, and its country the default destination. */
  wallet: RuntimeWallet;
  /** Every wallet the money may leave from. Without it, only `wallet`. */
  origins?: RuntimeWallet[];
  open: boolean;
  onClose: () => void;
  /** A payout was created: the balance of that wallet moved. */
  onSent?: (walletId: string) => void;
  /** Open on the camera, to pay a Bre-B QR. */
  startScanning?: boolean;
  /** Open on this corridor instead of the one of the wallet's currency. */
  initialDestination?: RuntimeCorridor;
}

type View = "form" | "review";

/** The rails whose recipient is typed in a form (Bre-B is a key the directory resolves, or a scanned QR). */
type FormRail = Exclude<SendRail, "BREB">;

interface RecipientDrafts {
  TRANSFIYA: TransfiyaRecipientValues;
  PIX: PixRecipientValues;
  SPEI: SpeiRecipientValues;
  CBU_CVU: CbuCvuRecipientValues;
}

const EMPTY_DRAFTS: RecipientDrafts = {
  TRANSFIYA: EMPTY_TRANSFIYA_VALUES,
  PIX: EMPTY_PIX_VALUES,
  SPEI: EMPTY_SPEI_VALUES,
  CBU_CVU: EMPTY_CBU_CVU_VALUES,
};

const RAIL_ICON: Record<SendRail, ReactNode> = {
  BREB: <Zap className="size-5" />,
  PIX: <Zap className="size-5" />,
  TRANSFIYA: <Landmark className="size-5" />,
  SPEI: <Landmark className="size-5" />,
  CBU_CVU: <Wallet className="size-5" />,
};

const RAIL_HINT = {
  BREB: "send_rail_breb_hint",
  TRANSFIYA: "send_rail_transfiya_hint",
  PIX: "send_rail_pix_hint",
  SPEI: "send_rail_spei_hint",
  CBU_CVU: "send_rail_cbu_cvu_hint",
} as const satisfies Record<SendRail, string>;

/** The corridor that pays in a wallet's own currency, when there is one. */
const corridorForCurrency = (currency: string): RuntimeCorridor | null =>
  RUNTIME_CORRIDOR_ISO3.find((iso3) => CURRENCY_BY_RUNTIME_CORRIDOR[iso3] === currency) ?? null;

function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</h3>;
}

/**
 * Sends money from any of the organization's wallets to someone in Colombia, Brazil, Mexico or Argentina,
 * nobody registered first: where from and where to, one amount, the recipient for the corridor's rail, an
 * optional concept. When the wallet and the corridor use different currencies the payout converts inside
 * itself at the quoted rate, so the whole send — conversion included — stays in this panel. A scanned Bre-B
 * QR fills the recipient (and the amount, when it carries one) and is paid as scanned.
 */
export function WalletSendPanel({
  wallet,
  origins,
  open,
  onClose,
  onSent,
  startScanning,
  initialDestination,
}: WalletSendPanelProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const { t: tPayins } = useTranslations("payins");
  const { t: tQr } = useTranslations("brebQrPay");
  const { user, can } = useAuth();
  const verification = useOrganizationVerificationStatus();
  const corridors = useAvailableCorridors();
  const qrPaymentsEnabled = canPayBrebQr(user);

  const originWallets = useMemo(() => {
    const active = (origins ?? []).filter((candidate) => candidate.status === "active");
    return active.some((candidate) => candidate.id === wallet.id) ? active : [wallet, ...active];
  }, [origins, wallet]);

  const [originId, setOriginId] = useState(wallet.id);
  const [country, setCountry] = useState<RuntimeCorridor>(
    () =>
      (startScanning ? "COL" : initialDestination) ??
      corridorForCurrency(wallet.currency) ??
      RUNTIME_CORRIDOR_ISO3[0],
  );
  const [view, setView] = useState<View>("form");
  const [reviewedBreakdown, setReviewedBreakdown] = useState<SendBreakdown | null>(null);
  const [amount, setAmount] = useState<MaskedAmount>(EMPTY_MASKED_AMOUNT);
  const [feeMode, setFeeMode] = useState<SendFeeMode>("sender");
  const [chosenRail, setChosenRail] = useState<SendRail | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [drafts, setDrafts] = useState<RecipientDrafts>(EMPTY_DRAFTS);
  const [formRecipients, setFormRecipients] = useState<Partial<Record<FormRail, SendRecipient | null>>>({});
  const [formKey, setFormKey] = useState(0);
  const [concept, setConcept] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [scanning, setScanning] = useState(!!startScanning && qrPaymentsEnabled);
  const [scanError, setScanError] = useState<{ title?: string; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const origin = originWallets.find((candidate) => candidate.id === originId) ?? wallet;
  const currency = CURRENCY_BY_RUNTIME_CORRIDOR[country];
  const rails = railsFor(country);
  const railAvailable = (candidate: SendRail) =>
    corridors.isAvailable(candidate, country, currency, "PAYOUT");
  const rail: SendRail =
    chosenRail && rails.includes(chosenRail) ? chosenRail : (rails.find(railAvailable) ?? rails[0]);
  const route = sendRouteFor(origin.currency, country, rail);
  const isColombia = country === "COL";

  const qr = useBrebQrResolution();
  const { resolve: resolveQr, reset: resetQr } = qr;
  const qrState = qr.state;
  const scannedQr = qrState.status === "resolved" ? qrState.data : null;
  // The QR's own amount is what its receiver expects: it is never typed, and the cost always goes on top.
  const qrAmount = isColombia ? (scannedQr?.amount ?? null) : null;
  const sendAmount = qrAmount ? maskDecimalString(qrAmount, maskForCurrency(currency)) : amount;
  const sendFeeMode: SendFeeMode = qrAmount ? "sender" : feeMode;
  const amountCurrency = amountCurrencyFor(route, sendFeeMode);

  const canWrite = can("payments:write");
  const gated = verification.isOrgMember && !verification.canCreatePayouts;
  const active = open && canWrite && !gated;

  const quoteState = useWalletSendQuote({
    amount: sendAmount.value == null ? null : String(sendAmount.value),
    feeMode: sendFeeMode,
    route,
    enabled: active && railAvailable(rail),
  });
  const quote = quoteState.quote && quoteMatchesSend(quoteState.quote, route) ? quoteState.quote : null;
  // While a refreshed price is on its way the review keeps showing the last one; the send button waits for it.
  const reviewQuote =
    quote ??
    (quoteState.lastQuote && quoteMatchesSend(quoteState.lastQuote, route) ? quoteState.lastQuote : null);
  const lastQuote =
    quoteState.lastQuote && quoteMatchesSend(quoteState.lastQuote, route) ? quoteState.lastQuote : null;

  const usingQr = rail === "BREB" && qrState.status !== "idle";
  const resolution = useBrebKeyResolution(null, keyInput, active && rail === "BREB" && !usingQr);
  const keyRecipient: BrebSendRecipient | null =
    rail === "BREB" && resolution.status === "resolved" && brebDirectoryOutcome(resolution) === "resolved"
      ? {
          rail: "BREB",
          keyType: resolvedBrebKeyType(resolution.data.key_type, keyInput.trim()),
          keyValue: keyInput.trim(),
          holderName: resolution.data.holder_name?.trim() || null,
          bankName: resolution.data.bank_name?.trim() || null,
        }
      : null;
  const qrRecipient: BrebSendRecipient | null =
    qrState.status === "resolved" && qrState.data.status === "ACTIVE"
      ? {
          rail: "BREB",
          keyType: resolvedBrebKeyType(qrState.data.key_type, qrState.data.key_value),
          keyValue: qrState.data.key_value,
          holderName: qrState.data.holder_name?.trim() || null,
          bankName: qrState.data.bank_name?.trim() || null,
          qr: { payload: qrState.qr, reference: qrState.data.id_qr },
        }
      : null;
  const recipient: SendRecipient | null =
    rail === "BREB" ? (usingQr ? qrRecipient : keyRecipient) : (formRecipients[rail] ?? null);

  const submit = useWalletSendSubmit({
    onQuoteExpired: quoteState.refresh,
    onQuoteSpent: quoteState.refresh,
  });

  const inOrigin = (value: string) => formatSendMoney(value, origin.currency, activeLanguage);
  const limitMoney = (value: string | null | undefined) =>
    value ? formatLimitAmount(value, currency, activeLanguage) : "";

  const limits = corridors.limitsFor(rail, country, currency, "PAYOUT");
  const payerType = sendPayerType(recipient);
  const bounds = boundsForPayer(limits, payerType);
  const delivered =
    quote?.amounts.payout_net ??
    (sendFeeMode === "sender" && sendAmount.value != null ? String(sendAmount.value) : null);
  const limitViolation = resolvePayoutLimitViolation({ limits, payerType, deliveredAmount: delivered });
  const insufficient =
    !!quote &&
    isDecimalString(origin.balance_available) &&
    compareDecimalStrings(quote.amounts.send, origin.balance_available) > 0;
  // A QR with an amount refuses any other, and the fee inversion can overshoot at a tier boundary.
  const qrAmountMismatch = !!qrAmount && !!quote && !quotePaysExactly(quote, qrAmount);

  // The bounds are on what arrives, so they are hinted only while that is what the field holds.
  const limitHint =
    !bounds || amountCurrency !== currency
      ? null
      : bounds.min_amount && bounds.max_amount
        ? tPayins("amount_limit_hint_range", {
            min: limitMoney(bounds.min_amount),
            max: limitMoney(bounds.max_amount),
          })
        : bounds.max_amount
          ? tPayins("amount_limit_hint_max", { max: limitMoney(bounds.max_amount) })
          : bounds.min_amount
            ? tPayins("amount_limit_hint_min", { min: limitMoney(bounds.min_amount) })
            : null;

  const quoteFieldError =
    quoteState.error && (isAmountLimitCode(quoteState.errorCode) || !quoteState.errorRetryable)
      ? quoteState.error
      : null;
  const fieldError = limitViolation
    ? limitViolation.kind === "above_maximum"
      ? tPayins("amount_limit_above", { max: limitMoney(limitViolation.bound) })
      : tPayins("amount_limit_below", { min: limitMoney(limitViolation.bound) })
    : qrAmountMismatch
      ? tQr("block_amount_mismatch")
      : insufficient && quote
        ? t("send_insufficient", {
            balance: inOrigin(origin.balance_available),
            debit: inOrigin(sendBreakdown(quote).debit),
          })
        : quoteFieldError;
  const quoteError = quoteState.error && !quoteFieldError ? quoteState.error || t("send_quote_error") : null;

  const canReview =
    active &&
    railAvailable(rail) &&
    !!quote &&
    !quoteState.loading &&
    !!recipient &&
    !limitViolation &&
    !insufficient &&
    !qrAmountMismatch;

  // One stable handler per form: each reports its values (kept for a remount) and its recipient once complete.
  const formHandlers = useMemo(() => {
    const handlerFor =
      <R extends FormRail>(formRail: R) =>
      (values: RecipientDrafts[R], next: SendRecipient | null) => {
        setDrafts((current) => ({ ...current, [formRail]: values }));
        setFormRecipients((current) => ({ ...current, [formRail]: next }));
      };
    return {
      TRANSFIYA: handlerFor("TRANSFIYA"),
      PIX: handlerFor("PIX"),
      SPEI: handlerFor("SPEI"),
      CBU_CVU: handlerFor("CBU_CVU"),
    };
  }, []);

  const clearQr = useCallback(() => {
    resetQr();
    setScanError(null);
  }, [resetQr]);

  const clearRecipient = () => {
    setKeyInput("");
    setDrafts(EMPTY_DRAFTS);
    setFormRecipients({});
    setFormKey((value) => value + 1);
  };

  // The amount is typed in one currency: a route that moves it to another starts the figure over.
  const keepAmountOnly = (nextCurrency: string) => {
    if (nextCurrency !== amountCurrency) setAmount(EMPTY_MASKED_AMOUNT);
  };

  const handleOriginChange = (walletId: string) => {
    const next = originWallets.find((candidate) => candidate.id === walletId);
    if (!next || next.id === origin.id) return;
    keepAmountOnly(amountCurrencyFor(sendRouteFor(next.currency, country, rail), sendFeeMode));
    setOriginId(next.id);
  };

  const handleDestinationChange = (iso3: string) => {
    const next = resolveRuntimeCorridor(iso3);
    if (!next || next === country) return;
    const nextRail = railsFor(next)[0];
    keepAmountOnly(amountCurrencyFor(sendRouteFor(origin.currency, next, nextRail), sendFeeMode));
    setCountry(next);
    setChosenRail(null);
    clearRecipient();
    clearQr();
  };

  // On a converted send the switch also moves the amount to the other currency: the same send is carried
  // over from the last price instead of reading the typed figure in a currency it was not typed in.
  const handleFeeModeChange = (mode: SendFeeMode) => {
    if (mode === feeMode) return;
    const nextCurrency = amountCurrencyFor(route, mode);
    if (nextCurrency !== amountCurrency) {
      const priced = quote ?? lastQuote;
      const carried = priced
        ? mode === "sender"
          ? (priced.amounts.payout_net ?? priced.amounts.receive)
          : priced.amounts.send
        : null;
      setAmount(carried ? maskDecimalString(carried, maskForCurrency(nextCurrency)) : EMPTY_MASKED_AMOUNT);
    }
    setFeeMode(mode);
  };

  const handleOpenChange = (next: boolean) => {
    if (next || submit.phase === "processing") return;
    onClose();
  };

  const handleSend = async () => {
    if (!quote || !recipient) return;
    const sent = await submit.submit({ quote, route, recipient, concept, feeMode: sendFeeMode });
    if (sent) onSent?.(origin.id);
  };

  const openReview = () => {
    setReviewedBreakdown(quote ? sendBreakdown(quote) : null);
    setView("review");
  };

  const readCode = useCallback(
    (raw: string) => {
      if (!isEmvQrPayload(raw)) {
        setScanError({ title: tQr("resolve_invalid_title"), text: tQr("scan_not_breb") });
        return;
      }
      setScanning(false);
      setScanError(null);
      setChosenRail("BREB");
      void resolveQr(raw);
    },
    [resolveQr, tQr],
  );

  const closeScanner = useCallback(() => setScanning(false), []);

  const readImage = async (file: Blob) => {
    setScanning(false);
    setScanError(null);
    try {
      const values = await detectQrInImage(file);
      const payload = values.find(isEmvQrPayload);
      if (payload) readCode(payload);
      else
        setScanError(
          values.length > 0
            ? { title: tQr("resolve_invalid_title"), text: tQr("scan_not_breb") }
            : { text: tQr("scan_image_no_qr") },
        );
    } catch {
      setScanError({ text: tQr("scan_image_failed") });
    }
  };

  const backToForm = () => {
    submit.releaseKey();
    submit.setPhase("idle");
    setView("form");
  };

  const startOver = (keepRecipient: boolean) => {
    submit.reset();
    setShowDetails(false);
    if (!keepRecipient) clearRecipient();
    // A QR is paid once: sending again goes to its key, and a fixed amount stays what it was.
    if (keepRecipient && scannedQr) {
      setKeyInput(scannedQr.key_value);
      if (qrAmount) setAmount(maskDecimalString(qrAmount, maskForCurrency(currency)));
    }
    clearQr();
    quoteState.refresh();
    setView("form");
  };

  const railOption = (value: SendRail) => {
    const available = railAvailable(value);
    return {
      value,
      label: formatRail(value),
      description: available
        ? t(RAIL_HINT[value])
        : t(colombiaRailUnavailableKey(value, corridors.reasonFor(value, country, currency, "PAYOUT"))),
      icon: RAIL_ICON[value],
      disabled: !available,
    };
  };

  const isDestinationAvailable = (iso3: string) => {
    const corridor = resolveRuntimeCorridor(iso3);
    if (!corridor) return false;
    const corridorCurrency = CURRENCY_BY_RUNTIME_CORRIDOR[corridor];
    return railsFor(corridor).some((candidate) =>
      corridors.isAvailable(candidate, corridor, corridorCurrency, "PAYOUT"),
    );
  };
  const isDestinationInMaintenance = (iso3: string) =>
    corridors.inMaintenance({ country: iso3, direction: "PAYOUT" });
  const destinationInMaintenance = isDestinationInMaintenance(country);

  const recipientForm = (() => {
    switch (rail) {
      case "BREB":
        return usingQr ? (
          <ScannedQrRecipient
            state={qrState}
            onRetry={() => void resolveQr(qrState.qr)}
            onScanAnother={() => setScanning(true)}
            onClear={clearQr}
          />
        ) : (
          <BrebKeyLookupField value={keyInput} onChange={setKeyInput} resolution={resolution} />
        );
      case "TRANSFIYA":
        return (
          <TransfiyaRecipientForm
            key={`tf-${formKey}`}
            defaults={drafts.TRANSFIYA}
            onChange={formHandlers.TRANSFIYA}
          />
        );
      case "PIX":
        return <PixRecipientForm key={`pix-${formKey}`} defaults={drafts.PIX} onChange={formHandlers.PIX} />;
      case "SPEI":
        return (
          <SpeiRecipientForm key={`spei-${formKey}`} defaults={drafts.SPEI} onChange={formHandlers.SPEI} />
        );
      case "CBU_CVU":
        return (
          <CbuCvuRecipientForm
            key={`cbu-${formKey}`}
            defaults={drafts.CBU_CVU}
            onChange={formHandlers.CBU_CVU}
          />
        );
    }
  })();

  // The money on its way, drawn from this send's route. The redeemed quote once the payout exists (a silent
  // re-price must not change the figures under a receipt), the reviewed one while it is being created.
  const transitQuote = submit.successData?.quoteData ?? reviewQuote;
  const transitBreakdown = transitQuote ? sendBreakdown(transitQuote) : null;
  const transit =
    recipient && transitBreakdown ? (
      <SendMoneyTransit
        route={route}
        recipientName={
          submit.successData?.recipientDisplay ??
          (recipient.rail === "BREB"
            ? (recipient.holderName ?? displayBrebKey(recipient.keyType, recipient.keyValue))
            : recipient.beneficiaryName)
        }
        debit={transitBreakdown.debit}
        arrives={transitBreakdown.arrives}
      />
    ) : undefined;

  const inResult = submit.phase !== "idle";

  const body = (() => {
    if (!canWrite) return <ColocaAlert variant="secondary" icon={Info} text={t("send_read_only")} />;
    if (gated) return <PayoutsVerificationGate status={verification} />;
    if (inResult) {
      return (
        <DispersionResultScreens
          phase={submit.phase as Exclude<typeof submit.phase, "idle">}
          successData={submit.successData}
          payoutStatus={submit.payoutStatus}
          countries={PAYOUT_WIZARD_COUNTRIES}
          submitError={submit.errorMessage}
          showDetails={showDetails}
          onToggleDetails={() => setShowDetails((value) => !value)}
          onSendAgain={() => startOver(true)}
          onSendToSomeoneElse={() => startOver(false)}
          onBackToWallets={onClose}
          onAdjustData={backToForm}
          onRetry={() => submit.setPhase("idle")}
          errorKind={submit.errorKind}
          organizationName={user?.organization?.name ?? null}
          transit={transit}
          processingTitle={
            isCrossCurrencySend(route)
              ? t("send_processing_fx", { from: route.originCurrency, to: route.currency })
              : undefined
          }
        />
      );
    }
    if (view === "review" && reviewQuote && recipient) {
      return (
        <SendReview
          quote={reviewQuote}
          route={route}
          recipient={recipient}
          concept={concept}
          reviewed={reviewedBreakdown}
        />
      );
    }
    const routeStrip = (
      <SendRouteStrip
        origins={originWallets}
        originId={origin.id}
        onOriginChange={handleOriginChange}
        destination={country}
        onDestinationChange={handleDestinationChange}
        isDestinationAvailable={isDestinationAvailable}
        isDestinationInMaintenance={isDestinationInMaintenance}
      />
    );
    if (destinationInMaintenance) {
      return (
        <div className="space-y-7">
          {routeStrip}
          <RailMaintenanceNotice kind="send" country={country} />
        </div>
      );
    }
    return (
      <div className="space-y-7">
        {routeStrip}

        <SendAmountField
          route={route}
          amount={sendAmount}
          onAmountChange={setAmount}
          feeMode={sendFeeMode}
          onFeeModeChange={handleFeeModeChange}
          quote={quote}
          lastQuote={lastQuote}
          loading={quoteState.loading}
          fieldError={sendAmount.value == null ? null : fieldError}
          quoteError={quoteError}
          limitHint={limitHint}
          onRetry={quoteState.refresh}
          autoFocus={!startScanning}
          lockedHint={qrAmount ? tQr("amount_fixed_hint") : null}
        />

        {isColombia && railAvailable("BREB") && qrPaymentsEnabled && !usingQr && (
          <div className="space-y-2">
            <ColocaButton
              type="button"
              variant="secondary"
              icon={ScanLine}
              onClick={() => setScanning(true)}
              className="w-full justify-center"
            >
              {tQr("scan_button")}
            </ColocaButton>
            {scanError && (
              <ColocaAlert
                variant="destructive"
                icon={ScanLine}
                heading={scanError.title}
                text={scanError.text}
              />
            )}
          </div>
        )}

        <section className="space-y-3">
          <SectionTitle>{t("send_rail_label")}</SectionTitle>
          <ColocaChoiceCards
            value={rail}
            onValueChange={(value) => {
              setChosenRail(value as SendRail);
              if (value !== "BREB") clearQr();
            }}
            columns={rails.length > 1 ? 2 : 1}
            aria-label={t("send_rail_label")}
            items={rails.map(railOption)}
          />
        </section>

        <section className="space-y-3">
          <SectionTitle>{t("send_recipient_label")}</SectionTitle>
          {recipientForm}
        </section>

        <section className="space-y-2">
          <ColocaLabel htmlFor="wallet-send-concept">{t("send_concept_label")}</ColocaLabel>
          <ColocaInput
            id="wallet-send-concept"
            value={concept}
            onValueChange={(value) => setConcept(value.slice(0, 120))}
            placeholder={t("step2_concept_placeholder")}
            maxLength={120}
            autoComplete="off"
          />
        </section>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void readImage(file);
          }}
        />
        {scanning && (
          <QrScannerOverlay
            onDetected={readCode}
            onClose={closeScanner}
            onUploadImage={() => fileInputRef.current?.click()}
          />
        )}
      </div>
    );
  })();

  const footer = (() => {
    if (!canWrite || gated) {
      return (
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("send_close")}
        </ColocaButton>
      );
    }
    if (inResult) return undefined;
    if (destinationInMaintenance) {
      return (
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("send_close")}
        </ColocaButton>
      );
    }
    if (view === "review" && reviewQuote && recipient) {
      return (
        <>
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={backToForm}>
            {t("send_review_edit")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            disabled={!quote || quoteState.loading}
            onClick={() => void handleSend()}
          >
            {t("send_confirm", {
              amount: isCrossCurrencySend(route)
                ? formatSendMoneyWithCode(sendBreakdown(reviewQuote).debit, origin.currency, activeLanguage)
                : inOrigin(sendBreakdown(reviewQuote).debit),
            })}
          </ColocaButton>
        </>
      );
    }
    return (
      <>
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("send_cancel")}
        </ColocaButton>
        <ColocaButton variant="primary" disabled={!canReview} onClick={openReview}>
          {t("send_review")}
        </ColocaButton>
      </>
    );
  })();

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={handleOpenChange}
      size="lg"
      pinLastChild={false}
      showCloseButton={submit.phase !== "processing"}
      title={view === "review" && !inResult ? t("send_review_title") : t("send_title")}
      description={t("send_subtitle", { currency: origin.currency })}
      footer={footer}
    >
      {body}
    </ColocaDrawer>
  );
}
